import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import {
  checkAndRequestPermission,
  isPushEnabled,
  registerDeviceForUser
} from '../services/onesignal';

/**
 * PushNotificationPrompt
 *
 * Shows a dismissible banner for users who haven't enabled push notifications.
 * Handles: Never prompted, previously denied, stale subscription
 */
const PushNotificationPrompt = () => {
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { theme } = useTheme();
  const [showBanner, setShowBanner] = useState(false);
  const [permissionState, setPermissionState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !user || !activeProfile?.id || dismissed) {
      return;
    }

    // Check if already dismissed this session
    if (sessionStorage.getItem('pushPromptDismissed') === 'true') {
      setDismissed(true);
      return;
    }

    const checkStatus = async () => {
      try {
        const OneSignal = window.plugins?.OneSignal || window.OneSignal;
        if (!OneSignal) {
          console.log('[PushPrompt] OneSignal not available');
          return;
        }

        // Check if push is enabled
        const enabled = await isPushEnabled();

        if (enabled) {
          // Already enabled - make sure device is registered
          await registerDeviceForUser(activeProfile.id);
          setPermissionState('enabled');
          setShowBanner(false);
          return;
        }

        // Not enabled - check why
        let permStatus = 'not_prompted';

        if (OneSignal.Notifications?.getPermissionAsync) {
          await new Promise((resolve) => {
            OneSignal.Notifications.getPermissionAsync((granted) => {
              permStatus = granted ? 'enabled' : 'not_prompted';
              resolve();
            });
          });
        }

        setPermissionState(permStatus);
        setShowBanner(true);

      } catch (err) {
        console.error('[PushPrompt] Error checking status:', err);
      }
    };

    // Delay check to let app stabilize
    const timer = setTimeout(checkStatus, 3000);
    return () => clearTimeout(timer);
  }, [user, activeProfile?.id, dismissed]);

  const handleEnableNotifications = async () => {
    try {
      const granted = await checkAndRequestPermission(activeProfile?.id);

      if (granted) {
        setPermissionState('enabled');
        setShowBanner(false);
      } else {
        setPermissionState('denied');
      }
    } catch (err) {
      console.error('[PushPrompt] Error requesting permission:', err);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setShowBanner(false);
    sessionStorage.setItem('pushPromptDismissed', 'true');
  };

  const handleOpenSettings = () => {
    if (window.cordova?.plugins?.settings) {
      window.cordova.plugins.settings.open('application_details');
    } else {
      alert('To enable notifications:\n\n1. Open Settings\n2. Find CardChase\n3. Tap Notifications\n4. Enable Allow Notifications');
    }
  };

  if (!showBanner) return null;

  const styles = {
    banner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#8b5cf6',
      color: 'white',
      padding: '12px 16px',
      margin: '8px 16px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: 1,
    },
    icon: {
      flexShrink: 0,
    },
    text: {
      flex: 1,
    },
    title: {
      fontWeight: '600',
      fontSize: '15px',
      margin: 0,
    },
    subtext: {
      margin: '2px 0 0 0',
      fontSize: '13px',
      opacity: 0.9,
    },
    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    button: {
      backgroundColor: 'white',
      color: '#8b5cf6',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
    },
    dismissButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      opacity: 0.7,
      cursor: 'pointer',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <div style={styles.icon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div style={styles.text}>
          {permissionState === 'denied' ? (
            <>
              <p style={styles.title}>Notifications are disabled</p>
              <p style={styles.subtext}>Enable in Settings to get trade alerts</p>
            </>
          ) : (
            <>
              <p style={styles.title}>Stay in the loop</p>
              <p style={styles.subtext}>Get notified about trades & messages</p>
            </>
          )}
        </div>
      </div>
      <div style={styles.actions}>
        {permissionState === 'denied' ? (
          <button style={styles.button} onClick={handleOpenSettings}>
            Settings
          </button>
        ) : (
          <button style={styles.button} onClick={handleEnableNotifications}>
            Enable
          </button>
        )}
        <button style={styles.dismissButton} onClick={handleDismiss}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
